import type { AspectSetSpec, AspectSpec, ResolutionTier } from "../types";
import { IconCheck } from "./icons";
import { cx } from "./ui";

function Preview({ w, h, on }: { w: number; h: number; on: boolean }) {
  const scale = 30 / Math.max(w, h);
  return (
    <span
      className={cx(
        "block rounded-[5px] border-2 transition",
        on ? "border-brand-500 bg-brand-200/70" : "border-slate-300 bg-slate-100",
      )}
      style={{ width: Math.max(9, w * scale), height: Math.max(9, h * scale) }}
    />
  );
}

export function AspectPicker({
  aspects,
  sets,
  selected,
  resolution,
  showPixels,
  onToggle,
  onApplySet,
}: {
  aspects: AspectSpec[];
  sets: AspectSetSpec[];
  selected: string[];
  resolution: ResolutionTier;
  /** 任意サイズ対応モデルだけを選んでいるとき、実ピクセル数を出す */
  showPixels: boolean;
  onToggle: (id: string) => void;
  onApplySet: (ids: string[]) => void;
}) {
  const setActive = (s: AspectSetSpec) =>
    s.aspects.length === selected.length && s.aspects.every((a) => selected.includes(a));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2">
        {aspects.map((a) => {
          const on = selected.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              title={`${a.hint}${showPixels ? ` / ${a.sizes[resolution].replace("x", "×")}px` : ""}`}
              onClick={() => onToggle(a.id)}
              aria-pressed={on}
              className={cx(
                "group relative flex h-[104px] flex-col items-center justify-center gap-2 rounded-xl border-2 bg-white transition",
                on
                  ? "border-brand-500 bg-brand-50 shadow-[0_0_0_3px_rgba(124,92,255,0.12)]"
                  : "border-slate-200 hover:border-brand-300 hover:bg-brand-50/40",
              )}
            >
              {on && (
                <span className="absolute right-1.5 top-1.5 grid h-4 w-4 place-items-center rounded-full bg-brand-500 text-white">
                  <IconCheck className="h-2.5 w-2.5" />
                </span>
              )}
              <span className="grid h-[34px] w-[34px] place-items-center">
                <Preview w={a.w} h={a.h} on={on} />
              </span>
              <span className={cx("text-[13px] font-bold", on ? "text-brand-700" : "text-slate-600")}>{a.label}</span>
              {showPixels && (
                <span className="absolute bottom-1.5 text-[10.5px] tabular-nums text-slate-400">
                  {a.sizes[resolution].replace("x", "×")}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {sets.map((s) => (
          <button
            key={s.id}
            type="button"
            title={s.hint}
            onClick={() => onApplySet(s.aspects)}
            className={cx("chip", setActive(s) && "border-brand-400 bg-brand-50 text-brand-700")}
          >
            <span className="font-semibold">{s.label}</span>
            <span className="text-slate-400">{s.aspects.join(" / ")}</span>
          </button>
        ))}
        {selected.length > 0 && (
          <button type="button" onClick={() => onApplySet([])} className="chip text-slate-400">
            クリア
          </button>
        )}
      </div>
    </div>
  );
}
